import React, { startTransition, useEffect, useState } from "react";
import { useAuth } from "../auth/FirebaseAuth.js";
import { useNavigate } from "react-router-dom";
import { getUserById } from "../models/UserFunctions.js";
import { getOrgById, getOrgByRef } from "../models/OrgFunctions.js";
import Row from "../layout/Row.js";
import Column from "../layout/Column.js";
import Divider from "../layout/Divider.js";
import Space from "../layout/Space.js";
import Icon1 from "../icons/Icon.js";
import MLTFGPT2 from "../textfields/MLTFGPT2.js";
import DropDown1 from "../dropdowns/DropDown1.js";
import CheckBoxGrey from "../textfields/CheckBoxGrey.js";
import PrimaryButton from "../buttons/PrimaryButton.js";
import createNewExpert from "../api/create/createNewExpert.js";
import { useSnackbar } from "notistack";
import { set } from "date-fns";
import SmallSpinner from "../loading/SmallSpinner.js";
import { Skeleton } from "@mui/material";
import { useAppContext } from "../config/AppContext.js";
import Modal1 from "../modals/Modal1.js";
import SecondaryButton from "../buttons/SecondaryButton.js";
import {
  getDocDataById,
  getDocDataByRef,
  queryCollection,
  updateDocDataField,
} from "../models/DataFunctions.js";
import sendInvite from "../api/send/sendInvite.js";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi.js";
import TextField2 from "../textfields/TextField2.js";
import MLTFGPT1 from "../textfields/MLTFGPT1.js";
import createDocApi from "../api/database/createDocApi.js";
import deleteDocApi from "../api/database/deleteDocApi.js";

function NewFeatures({ navigateWorkspace }) {
  const { currentUser, loading } = useAuth();

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [features, setFeatures] = useState([]);
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    if (currentUser) {
      try {
        // Fetch user data
        const user = await getUserById(currentUser.uid);

        // Fetch organization data using user's org reference
        const org = await getOrgById(user.org_id);
        const features = await queryCollection("feature", []);
        setFeatures(features);

        // Update state with fetched data
        setUserData(user);
        setOrgData(org);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  useEffect(() => {
    if (currentUser) {
      handleFetchData(); // Fetch user data when currentUser is available
    }
  }, [currentUser]); // Dependency array ensures this runs when currentUser changes

  // State variables to store the input values
  const handleCreateFeature = async () => {
    try {
      const feature = {
        title,
        description,
        image,
        order: features.length,
      };
      await createDocApi("feature", feature, prefix);
      handleClickVariant("Fonctionnalité ajoutée", "success");
      setOpen(false);
      handleFetchData();
    } catch (error) {
      console.error("Error creating feature:", error);
      handleClickVariant(
        "Erreur lors de l'ajout de la fonctionnalité",
        "error"
      );
    }
  };

  const handleDeleteFeature = async (ref) => {
    try {
      await deleteDocApi(ref, prefix);
      handleClickVariant("Fonctionnalité supprimée", "success");
      handleFetchData();
    } catch (error) {
      console.error("Error deleting feature:", error);

      handleClickVariant(
        "Erreur lors de la suppression de la fonctionnalité",
        "error"
      );
    }
  };

  const handleUpFeature = async (ref) => {
    try {
      // Récupération de la fonctionnalité actuelle
      const feature = await getDocDataByRef(ref);

      if (!feature || feature.order === undefined) {
        handleClickVariant(
          "Impossible de récupérer la fonctionnalité",
          "error"
        );
        return;
      }

      // Trouver la fonctionnalité précédente avec un order inférieur
      const sortedFeatures = [...features].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      const currentIndex = sortedFeatures.findIndex((f) => f.ref === ref);
      if (currentIndex <= 0) {
        handleClickVariant("Fonctionnalité déjà en haut", "error");
        return;
      }

      const nextFeature = sortedFeatures[currentIndex - 1];

      // Échanger les valeurs d'ordre entre la fonctionnalité actuelle et la précédente
      await updateDocDataFieldApi(ref, "order", nextFeature.order, prefix);
      await updateDocDataFieldApi(
        nextFeature.ref,
        "order",
        feature.order,
        prefix
      );

      // Notification de succès et rechargement des données
      handleClickVariant("Fonctionnalité déplacée", "success");
      handleFetchData();
    } catch (error) {
      console.error("Error moving feature:", error);
      handleClickVariant(
        "Erreur lors du déplacement de la fonctionnalité",
        "error"
      );
    }
  };

  const handleDownFeature = async (ref) => {
    try {
      // invert the following feature and this one order
      const feature = await getDocDataByRef(ref);

      const nextFeature = features
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .filter((f) => f.order > feature.order)[0];
      if (nextFeature.length === 0) {
        handleClickVariant("Fonctionnalité déjà en bas", "error");
        return;
      }
      await updateDocDataFieldApi(ref, "order", nextFeature[0].order, prefix);
      await updateDocDataFieldApi(
        nextFeature[0].ref,
        "order",
        feature.order,
        prefix
      );

      handleClickVariant("Fonctionnalité déplacée", "success");
      handleFetchData();
    } catch (error) {
      console.error("Error deleting feature:", error);

      handleClickVariant(
        "Erreur lors du déplacement de la fonctionnalité",
        "error"
      );
    }
  };

  if (!userData) return <div className="w-screen h-screen bg-black"></div>;

  return (
    <div className="bg-black h-screen w-screen flex flex-col  items-center text-center overflow-y-auto overflow-x-hidden">
      <Modal1 onClose={() => setOpen(false)} isOpen={open}>
        <div className="bg-white rounded-xl gap-4 p-4 w-c500 h-fit flex flex-col items-center justify-center">
          <TextField2
            value={title}
            label={"Titre"}
            onChange={(e) => setTitle(e.target.value)}
          />
          <MLTFGPT1
            value={description}
            label={"Description"}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
          />
          <TextField2
            value={image}
            label={"Image"}
            onChange={(e) => setImage(e.target.value)}
          />
          <PrimaryButton className={"w-fit"} onClick={handleCreateFeature}>
            Ajouter
          </PrimaryButton>
        </div>
      </Modal1>
      <div
        onClick={() => startTransition(() => navigateWorkspace(-1))}
        className="absolute bottom-20 cursor-pointer hover:bg-black hover:text-white hover:border-1 hover:border-white right-20 px-10 py-2 bg-white rounded-full shadow-xl shadow-wshadow_50 mt-4 ml-4"
      >
        Fermer
      </div>
      {userData.email === "william.bresso@gmail.com" && (
        <div
          onClick={() => setOpen(true)}
          className="absolute bottom-40 cursor-pointer hover:bg-black hover:text-white hover:border-1 hover:border-white right-20 px-10 py-2 bg-white rounded-full shadow-xl shadow-wshadow_50 mt-4 ml-4"
        >
          Ajouter
        </div>
      )}

      <div className="h-c400 flex flex-col items-center text-center w-screen bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
        <div className="text-hx6 text-white mt-28">Nouveautés sur hexagone</div>
        <div className="text-hx2 text-wshadow_75 mt-4 w-c500">
          Découvrez toutes les nouvelles fonctionnalités qui arrivent sur
          hexagone
        </div>
        <div className="text-hx2 text-wshadow_50 mt-4 w-c500">
          - Février 2025 -
        </div>

        <img
          src="/favicon.ico"
          alt="logo"
          className="w-20 h-20 mt-10 rounded-xl shadow-xl shadow-wshadow_50 "
        />
      </div>
      <div className="w-fit h-fit flex  flex-col items-start  mb-64 justify-start">
        {features
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((feature, index) =>
            index % 2 === 0 ? (
              <div key={index} className="flex flew-row gap-24 mt-48">
                <div className="w-c500 h-fit text-left flex flex-col gap-4 items-start justify-start ">
                  <div className="text-hx4 text-wshadow_90 font-medium">
                    {feature.title}
                  </div>
                  <div className="text-h1 text-wshadow_80">
                    {feature.description}
                  </div>{" "}
                  {userData.email === "william.bresso@gmail.com" && (
                    <div className="flex flex-col gap-1">
                      <SecondaryButton
                        onClick={() => handleDeleteFeature(feature.ref)}
                        className={"w-fit"}
                      >
                        Supprimer
                      </SecondaryButton>{" "}
                      <SecondaryButton
                        onClick={() => handleUpFeature(feature.ref)}
                        className={"w-fit"}
                      >
                        Up
                      </SecondaryButton>{" "}
                      <SecondaryButton
                        onClick={() => handleDownFeature(feature.ref)}
                        className={"w-fit"}
                      >
                        Down
                      </SecondaryButton>{" "}
                    </div>
                  )}
                </div>
                <div className="w-c500 h-fit flex flex-col items-start justify-start ">
                  <img
                    src={feature.image}
                    alt="feature"
                    className="w-c500 h-auto rounded-3xl  object-contain "
                  />
                </div>{" "}
              </div>
            ) : (
              <div key={index} className="flex flew-row gap-24 mt-48">
                <div className="w-c500 h-fit flex flex-col items-start justify-start ">
                  <img
                    src={feature.image}
                    alt="feature"
                    className="w-c500 h-auto   object-contain rounded-3xl  "
                  />
                </div>{" "}
                <div className="w-c500 h-fit text-left flex flex-col gap-4 items-start justify-start ">
                  <div className="text-hx4 text-wshadow_90 font-medium">
                    {feature.title}
                  </div>
                  <div className="text-h1 text-wshadow_80">
                    {feature.description}
                  </div>
                  {userData.email === "william.bresso@gmail.com" && (
                    <div className="flex flex-col gap-1">
                      <SecondaryButton
                        onClick={() => handleDeleteFeature(feature.ref)}
                        className={"w-fit"}
                      >
                        Supprimer
                      </SecondaryButton>{" "}
                      <SecondaryButton
                        onClick={() => handleUpFeature(feature.ref)}
                        className={"w-fit"}
                      >
                        Up
                      </SecondaryButton>{" "}
                      <SecondaryButton
                        onClick={() => handleDownFeature(feature.ref)}
                        className={"w-fit"}
                      >
                        Down
                      </SecondaryButton>{" "}
                    </div>
                  )}
                </div>
              </div>
            )
          )}
      </div>
    </div>
  );
}

export default NewFeatures;
